import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Save, Download, ArrowLeft, Star, Plus } from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import { useApi } from '../contexts/ApiContext';
import { useAIOperation } from '../hooks/useAIOperation';
import { ProviderRegistry } from '../services/providerRegistry';
import { ReplicateProvider } from '../services/providers/ReplicateProvider';
import ImagePicker from '../components/ImagePicker';
import ProviderToggle from '../components/ProviderToggle';
import JobStatus, { CostBadge } from '../components/JobStatus';
import ScenePresets, { SCENE_PRESETS, type ScenePreset } from '../components/ScenePresets';
import type { MedaImage } from '../types/image';

interface ResultTile {
  blob: Blob;
  url: string;
  savedId?: string;
  favorite: boolean;
}

export default function VariationsPage() {
  const navigate = useNavigate();
  const { images, addImage, updateImage, toggleFavorite } = useLibrary();
  const { tier, setTier, apiKeys } = useApi();
  const [opState, opActions] = useAIOperation();

  const [selectedImage, setSelectedImage] = useState<MedaImage | null>(null);
  const [presetId, setPresetId] = useState<string | null>('outdoor');
  const [customPrompt, setCustomPrompt] = useState('');
  const [count, setCount] = useState<2 | 3 | 4>(2);
  const [randomisePoses, setRandomisePoses] = useState(true);
  const [results, setResults] = useState<ResultTile[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  const identityImages = useMemo(
    () => images.filter(i => i.tags?.includes('identity')),
    [images],
  );

  const registry = useMemo(() => new ProviderRegistry({
    huggingfaceKey: apiKeys.huggingface,
    replicateKey: apiKeys.replicate,
    preferredTier: tier,
  }), [apiKeys.huggingface, apiKeys.replicate, tier]);

  const provider = useMemo(() => registry.getBestProvider('face_variation'), [registry]);
  const providers = useMemo(() => registry.getProvidersForOp('face_variation'), [registry]);
  const hasFree = providers.some(p => p.info.tier === 'free');
  const hasPaid = providers.some(p => p.info.tier === 'paid');

  const preset = useMemo(
    () => SCENE_PRESETS.find(p => p.id === presetId) ?? null,
    [presetId],
  );

  const promptText = useMemo(() => {
    const custom = customPrompt.trim();
    if (custom) return custom;
    return preset?.prompt ?? '';
  }, [customPrompt, preset]);

  const estimatedCost = useMemo(() => {
    if (!provider) return 0;
    if (provider.info.tier === 'free') return 0;
    if (provider instanceof ReplicateProvider) return provider.getEstimatedCost('face_variation') * count;
    return 0.05 * count;
  }, [provider, count]);

  useEffect(() => () => {
    results.forEach(r => URL.revokeObjectURL(r.url));
  }, [results]);

  const handleSelect = useCallback((image: MedaImage) => {
    setSelectedImage(image);
    opActions.reset();
  }, [opActions]);

  const handleUploadFile = useCallback(async (file: File) => {
    const img = await addImage(file, file.name);
    handleSelect(img);
  }, [addImage, handleSelect]);

  const handleSelectIdentity = useCallback((img: MedaImage) => {
    setSelectedImage(img);
    opActions.reset();
  }, [opActions]);

  const isIdentity = !!selectedImage?.tags?.includes('identity');

  const handleToggleIdentityTag = useCallback(async () => {
    if (!selectedImage) return;
    const existing = selectedImage.tags ?? [];
    const nextTags = isIdentity
      ? existing.filter(t => t !== 'identity')
      : [...existing, 'identity'];
    await updateImage(selectedImage.id, { tags: nextTags });
    setSelectedImage({ ...selectedImage, tags: nextTags });
  }, [selectedImage, isIdentity, updateImage]);

  const handleGenerate = useCallback(async () => {
    if (!selectedImage || !provider || !promptText) return;
    results.forEach(r => URL.revokeObjectURL(r.url));
    setResults([]);
    const all: ResultTile[] = [];
    for (let i = 0; i < count; i++) {
      setCurrentIndex(i);
      const blob = await opActions.run(provider, 'face_variation', selectedImage.blob, {
        prompt: promptText,
        style: preset?.style ?? 'Photographic (Default)',
        count: 1,
        randomise_poses: randomisePoses,
      });
      if (!blob) break;
      const tile: ResultTile = {
        blob,
        url: URL.createObjectURL(blob),
        favorite: false,
      };
      all.push(tile);
      setResults([...all]);
    }
    setCurrentIndex(-1);
  }, [selectedImage, provider, promptText, preset, count, randomisePoses, opActions, results]);

  const handleSaveTile = useCallback(async (index: number) => {
    if (!selectedImage) return;
    const tile = results[index];
    if (!tile || tile.savedId) return;
    const label = `${selectedImage.name} (variation ${index + 1})`;
    const saved = await addImage(
      tile.blob,
      label,
      'generated',
      selectedImage.id,
      'face_variation',
    );
    setResults(prev => prev.map((r, i) => i === index ? { ...r, savedId: saved.id } : r));
  }, [selectedImage, results, addImage]);

  const handleFavoriteTile = useCallback(async (index: number) => {
    const tile = results[index];
    if (!tile) return;
    // Ensure saved first, then toggle favorite on the saved image
    let savedId = tile.savedId;
    if (!savedId && selectedImage) {
      const label = `${selectedImage.name} (variation ${index + 1})`;
      const saved = await addImage(
        tile.blob,
        label,
        'generated',
        selectedImage.id,
        'face_variation',
      );
      savedId = saved.id;
    }
    if (savedId) {
      await toggleFavorite(savedId);
    }
    setResults(prev => prev.map((r, i) =>
      i === index ? { ...r, savedId, favorite: !r.favorite } : r,
    ));
  }, [results, selectedImage, addImage, toggleFavorite]);

  const handleDownloadTile = useCallback((index: number) => {
    const tile = results[index];
    if (!tile || !selectedImage) return;
    const a = document.createElement('a');
    a.href = tile.url;
    a.download = `${selectedImage.name}_variation_${index + 1}.png`;
    a.click();
  }, [results, selectedImage]);

  const noProvider = !provider;
  const needsKey = noProvider && !apiKeys.replicate;
  const running = opState.status === 'submitting' || opState.status === 'processing';
  const canGenerate = !!(selectedImage && isIdentity && provider && promptText && !running);

  const gridCols = count === 2 ? 'grid-cols-2' : count === 3 ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <div className="flex flex-col h-full sage-animate-fade-in">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
      >
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg" style={{ color: 'var(--sage-text-muted)' }}>
          <ArrowLeft size={18} />
        </button>
        <Users size={18} style={{ color: 'var(--sage-primary)' }} />
        <h1 className="text-sm font-medium flex-1">Variations</h1>
        <ProviderToggle tier={tier} onToggle={setTier} freeAvailable={hasFree} paidAvailable={hasPaid} />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left panel — controls */}
        <div
          className="w-72 p-4 border-r overflow-y-auto flex flex-col gap-5"
          style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
        >
          {/* 1. Reference picker */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--sage-text-muted)' }}>
                Reference Photo
              </p>
              <span className="text-[10px]" style={{ color: 'var(--sage-text-muted)' }}>
                {identityImages.length} tagged
              </span>
            </div>

            <p className="text-[11px] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Use a clear photo of the whole person — full body or portrait works best.
              Face-only is fine too.
            </p>

            {identityImages.length > 0 ? (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {identityImages.map(img => (
                  <button
                    key={img.id}
                    onClick={() => handleSelectIdentity(img)}
                    className="aspect-square rounded-lg overflow-hidden transition-all"
                    style={{
                      border: selectedImage?.id === img.id
                        ? '2px solid var(--sage-primary)'
                        : '2px solid transparent',
                      opacity: selectedImage?.id === img.id ? 1 : 0.7,
                    }}
                  >
                    <img
                      src={URL.createObjectURL(img.thumbnail)}
                      alt={img.name}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[11px] mb-3" style={{ color: 'var(--sage-text-muted)' }}>
                Select an image below and tag it as a reference photo.
              </p>
            )}

            <ImagePicker
              selectedId={selectedImage?.id ?? null}
              onSelect={handleSelect}
              onUpload={handleUploadFile}
            />

            {selectedImage && (
              <button
                onClick={handleToggleIdentityTag}
                className="w-full mt-2 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs transition-colors"
                style={{
                  backgroundColor: isIdentity
                    ? 'color-mix(in srgb, var(--sage-primary) 15%, var(--sage-bg))'
                    : 'var(--sage-bg)',
                  color: isIdentity ? 'var(--sage-primary)' : 'var(--sage-text-muted)',
                  border: `1px solid ${isIdentity ? 'var(--sage-primary)' : 'var(--sage-white-10)'}`,
                }}
              >
                <Plus size={12} />
                {isIdentity ? 'Reference photo (tap to remove)' : 'Use as reference'}
              </button>
            )}
          </div>

          {/* 2. Scene presets + custom prompt */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Scene
            </p>
            <ScenePresets
              selectedId={presetId}
              onSelect={p => setPresetId(p?.id ?? null)}
            />
            <div className="mt-3">
              <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
                Or custom prompt
              </p>
              <textarea
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                placeholder="e.g. the character sitting at a cafe in Paris, full body..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg text-xs resize-none outline-none"
                style={{
                  backgroundColor: 'var(--sage-bg)',
                  color: 'var(--sage-text)',
                  border: '1px solid var(--sage-white-10)',
                }}
              />
              <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
                Tip: refer to the subject as "the character". Specify "full body" or
                "three-quarter length" for whole-person shots.
              </p>
            </div>
          </div>

          {/* Randomise poses toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={randomisePoses}
              onChange={e => setRandomisePoses(e.target.checked)}
              className="rounded"
            />
            <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
              Randomise poses across variations
            </span>
          </label>

          {/* 3. Count selector */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sage-text-muted)' }}>
              Number of Variations
            </p>
            <div className="flex gap-2">
              {([2, 3, 4] as const).map(n => (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: count === n ? 'var(--sage-primary)' : 'var(--sage-bg)',
                    color: count === n ? '#1c1b1a' : 'var(--sage-text-muted)',
                    border: `1px solid ${count === n ? 'var(--sage-primary)' : 'var(--sage-white-10)'}`,
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* 4. Provider */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: 'var(--sage-text-muted)' }}>
              Provider
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--sage-text)' }}>
                {provider?.info.name || 'None configured'}
              </span>
              <CostBadge cost={estimatedCost} tier={provider?.info.tier || 'paid'} />
            </div>
            {!hasPaid && (
              <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
                Variations require a paid provider (Replicate Consistent Character).
              </p>
            )}
          </div>

          {/* 5. Primary action */}
          {needsKey ? (
            <div
              className="p-3 rounded-lg text-xs"
              style={{ backgroundColor: 'color-mix(in srgb, var(--sage-accent) 10%, var(--sage-bg))', color: 'var(--sage-accent)' }}
            >
              Add a Replicate API key in Settings to enable variations.
              <button onClick={() => navigate('/settings')} className="underline ml-1">Open Settings</button>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'var(--sage-primary)', color: '#1c1b1a' }}
            >
              {running
                ? currentIndex >= 0
                  ? `Generating ${currentIndex + 1} of ${count}...`
                  : 'Processing...'
                : `Generate ${count} Variation${count > 1 ? 's' : ''}`}
            </button>
          )}

          {/* 6. Job status */}
          <JobStatus
            status={opState.status}
            progress={
              running && currentIndex >= 0
                ? `Generating ${currentIndex + 1} of ${count}... ${opState.progress}`
                : opState.progress
            }
            error={opState.error}
            onRetry={handleGenerate}
          />
        </div>

        {/* Main area — results grid or identity preview */}
        <div className="flex-1 p-6 min-w-0 overflow-auto">
          {!selectedImage ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Users size={48} style={{ color: 'var(--sage-white-10)' }} className="mx-auto mb-3" />
                <p className="text-sm" style={{ color: 'var(--sage-text-muted)' }}>
                  Select a reference photo to start
                </p>
              </div>
            </div>
          ) : results.length > 0 ? (
            <div className="w-full max-w-5xl mx-auto">
              <h2 className="text-sm font-medium mb-4" style={{ color: 'var(--sage-text)' }}>
                Variations ({results.length} of {count})
              </h2>
              <div className={`grid ${gridCols} gap-4`}>
                {results.map((tile, index) => (
                  <div
                    key={index}
                    className="relative rounded-xl overflow-hidden group"
                    style={{ backgroundColor: 'var(--sage-surface)', border: '1px solid var(--sage-white-10)' }}
                  >
                    <img
                      src={tile.url}
                      alt={`Variation ${index + 1}`}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute top-2 left-2">
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: 'color-mix(in srgb, var(--sage-bg) 80%, transparent)',
                          color: 'var(--sage-text)',
                        }}
                      >
                        #{index + 1}
                      </span>
                    </div>
                    <button
                      onClick={() => handleFavoriteTile(index)}
                      className="absolute top-2 right-2 p-1.5 rounded-lg transition-colors"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--sage-bg) 80%, transparent)',
                        color: tile.favorite ? 'var(--sage-accent)' : 'var(--sage-text-muted)',
                      }}
                      aria-label="Favorite"
                    >
                      <Star size={14} fill={tile.favorite ? 'currentColor' : 'none'} />
                    </button>
                    <div className="absolute bottom-2 right-2 flex gap-1.5">
                      <button
                        onClick={() => handleDownloadTile(index)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{
                          backgroundColor: 'color-mix(in srgb, var(--sage-bg) 80%, transparent)',
                          color: 'var(--sage-text-muted)',
                        }}
                        aria-label="Download"
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => handleSaveTile(index)}
                        disabled={!!tile.savedId}
                        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-60"
                        style={{
                          backgroundColor: tile.savedId
                            ? 'color-mix(in srgb, var(--sage-primary) 20%, var(--sage-bg))'
                            : 'var(--sage-primary)',
                          color: tile.savedId ? 'var(--sage-primary)' : '#1c1b1a',
                        }}
                        aria-label="Save"
                      >
                        <Save size={12} />
                        {tile.savedId ? 'Saved' : 'Save'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="max-w-md text-center">
                <img
                  src={URL.createObjectURL(selectedImage.thumbnail)}
                  alt={selectedImage.name}
                  className="rounded-xl shadow-lg max-h-[60vh] object-contain mx-auto"
                />
                <p className="text-xs mt-3" style={{ color: 'var(--sage-text-muted)' }}>
                  {selectedImage.name}
                </p>
                {!isIdentity && (
                  <p className="text-xs mt-2" style={{ color: 'var(--sage-accent)' }}>
                    Tag this image as a reference photo to enable generation.
                  </p>
                )}
                {isIdentity && (
                  <p className="text-xs mt-2" style={{ color: 'var(--sage-text-muted)' }}>
                    Pick a scene and click Generate.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
